// THE PUBLIC FOOTER — the dark band under every logged-out page.
//
// Lifted out of pages/homePage/Home.jsx together with PublicNav (22 Sep 2026)
// so the home page and the four static pages cannot drift apart.
//
// The Solutions and Services columns are SECTION LINKS, not routes: they scroll
// on the home page and navigate home from anywhere else, exactly like the nav.
// The "For patients" column exists only when this deployment has a patient site
// (`VITE_PATIENT_WEB_URL`); pointing at nothing would be worse than saying
// nothing. The three social names are NOT LINKS (22 Sep 2026): they had no
// URLs, only `href="#"`, so clicking one jumped to the top of the page and
// looked broken. They stay as plain text until real accounts exist — inventing
// handles would be worse, and a link that goes nowhere is worse than a label.

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { PATIENT_WEB_URL } from '../../utils/patientWebUrl';
import logo from '../../assets/img/logo-TT2.png';

const SOLUTION_LINKS = [
  'solution_hospital_title',
  'solution_clinic_title',
  'solution_solo_title',
  'solution_pharmacy_title',
  'solution_lab_title',
  'solution_radiology_title',
];

const SERVICE_LINKS = [
  'service_medi_consult_title',
  'service_pharma_title',
  'service_lab_title',
  'service_radiology_title',
  'service_care_taker_title',
];

const colHead = 'mb-4 text-[11px] font-bold uppercase tracking-[0.13em] text-gray-500';
const colLink = 'cursor-pointer text-sm text-gray-400 transition-colors hover:text-[#4ABDBD]';
// The social names: same size and colour as a footer link, but no pointer and
// no hover, because nothing happens when you click them.
const colText = 'text-sm text-gray-400';

export default function PublicFooter() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === '/' || location.pathname === '/home';

  const goSection = (id) => (e) => {
    e.preventDefault();
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (onHome && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate('/', { state: { scrollTo: id } });
  };

  return (
    <footer className="w-full bg-gray-900 text-white" data-testid="public-footer">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-12">
          <div className="col-span-2 lg:col-span-4">
            <div className="mb-4 flex items-center gap-2">
              <img src={logo} alt="" className="h-9 w-9 object-contain" />
              <span className="text-xl font-bold text-[#4ABDBD]">{t('home.brand_name')}</span>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-gray-400" data-testid="public-footer-about">
              {t('home.footer_about')}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h3 className={colHead}>{t('home.footer_solutions')}</h3>
            <ul className="space-y-2.5">
              {SOLUTION_LINKS.map((key) => (
                <li key={key}>
                  <a href="#solutions" onClick={goSection('solutions')} className={colLink}>
                    {t(`home.${key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className={colHead}>{t('home.footer_services')}</h3>
            <ul className="space-y-2.5">
              {SERVICE_LINKS.map((key) => (
                <li key={key}>
                  <a href="#services" onClick={goSection('services')} className={colLink}>
                    {t(`home.${key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className={colHead}>{t('home.footer_company')}</h3>
            <ul className="space-y-2.5">
              <li><Link to="/about" className={colLink} data-testid="public-footer-about-link">{t('home.footer_about_us')}</Link></li>
              <li><Link to="/contact" className={colLink} data-testid="public-footer-contact-link">{t('home.footer_contact')}</Link></li>
            </ul>
            <h3 className={`${colHead} mt-8`}>{t('home.footer_legal')}</h3>
            <ul className="space-y-2.5">
              <li><Link to="/terms" className={colLink} data-testid="public-footer-terms-link">{t('home.footer_terms')}</Link></li>
              <li><Link to="/privacy" className={colLink} data-testid="public-footer-privacy-link">{t('home.footer_privacy')}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            {PATIENT_WEB_URL && (
              <>
                <h3 className={colHead}>{t('home.footer_for_patients')}</h3>
                <a
                  href={`${PATIENT_WEB_URL}/#/`}
                  className="mb-8 inline-flex items-center gap-1.5 rounded-xl border border-gray-700 px-3 py-2.5 text-xs font-semibold text-gray-300 transition-colors hover:border-[#4ABDBD] hover:text-[#4ABDBD]"
                  data-testid="public-footer-patient-link"
                >
                  {t('home.footer_patient_link')} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </>
            )}
            <h3 className={colHead}>{t('home.footer_follow')}</h3>
            <ul className="space-y-2.5">
              <li><span className={colText} data-testid="public-footer-facebook">{t('home.facebook')}</span></li>
              <li><span className={colText} data-testid="public-footer-twitter">{t('home.twitter')}</span></li>
              <li><span className={colText} data-testid="public-footer-linkedin">{t('home.linkedin')}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-gray-800 pt-6 text-center text-xs text-gray-500">
          <p data-testid="public-footer-copyright">{t('home.footer_copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  );
}
